import React, { useState, useEffect } from "react"
import { Col, Row } from "reactstrap"
import { Button } from "primereact/button"
import { Dialog } from "primereact/dialog"
import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Sidebar } from "primereact/sidebar"
import { Tooltip } from "primereact/tooltip"
import axios from "axios"
import { triggerAlert } from "utils/CommonFunctions"
import { TreeSelect } from "primereact/treeselect"

const AddContact = ({ selectedCompany }) => {
  const [visibleRightContacts, setVisibleRightContacts] = useState(false)
  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    job_title: "",
    company: null,
    department: null,
    category: null,
    group: null,
    country: null,
    state: null,
    city: null,
    street_address: "",
    street_address_line_2: "",
    pincode: "",
    related_person: null,
    status: "Active",
    label: null,
    private: false,
    user_id: "",
    password: "",
    address: "", // Initialize address as empty
  })

  const [formErrors, setFormErrors] = useState({})
  const [companies, setCompanies] = useState([])
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])
  const [departments, setDepartments] = useState([])
  const [hiringManagers, setHiringManagers] = useState([])
  const [countries, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  // const [labels] = useState([
  //   { label: "Work from Office (WFO)", value: "WORK" },
  //   { label: "Work from Home (WFH)", value: "HOME" },
  //   { label: "Work from Remote (WFR)", value: "REMOTE" },
  // ]);

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [visible, setVisible] = useState(false)
  const [categoryNodes, setCategoryNodes] = useState([])
  const [selectedCategoryNode, setSelectedCategoryNode] = useState(null)
  const [groupNodes, setGroupNodes] = useState([])
  const [selectedGroupNode, setSelectedGroupNode] = useState(null)
  const [tempAddressData, setTempAddressData] = useState({
    // Temporary state for address dialog
    street_address: "",
    street_address_line_2: "",
    country: null,
    state: null,
    city: null,
    pincode: "",
    label: null,
  })

  // Validation patterns
  const patterns = {
    name: /^[a-zA-Z\s]{2,50}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[\d+\-\s]{7,15}$/,
    job_title: /^[a-zA-Z0-9\s]{2,100}$/,
    address: /^[a-zA-Z0-9\s,.-]{5,100}$/,
    pincode: /^[a-zA-Z0-9]{0,6}$/,
    // user_id: /^[A-Za-z][A-Za-z\s&-]{2,50}$/,
    // password: /^[A-Za-z][A-Za-z\s&-]{2,50}$/,
  }

  // Validate a single field
  const validateField = (name, value) => {
    let error = ""
    switch (name) {
      case "first_name":
        if (!value) error = "Required"
        else if (!patterns.name.test(value))
          error = "Invalid name (letters and spaces only, 2-50 chars)"
        break
      case "middle_name":
        if (value && !patterns.name.test(value))
          error = "Invalid name (letters and spaces only, 2-50 chars)"
        break
      case "last_name":
        if (value && !patterns.name.test(value))
          error = "Invalid name (letters and spaces only, 2-50 chars)"
        break
      case "email":
        if (!value) error = "Required"
        else if (!patterns.email.test(value)) error = "Invalid email format"
        break
      case "phone_number":
        if (value && !patterns.phone.test(value))
          error = "Invalid phone number (7-15 digits, +, -, or spaces)"
        break
      case "job_title":
        if (value && !patterns.job_title.test(value))
          error = "Invalid job title (alphanumeric, 2-100 chars)"
        break
      case "street_address":
      case "street_address_line_2":
        if (value && !patterns.address.test(value))
          error = "Invalid address (5-100 chars)"
        break
      case "pincode":
        if (value && !patterns.pincode.test(value))
          error = "Invalid postal code (0-6 chars)"
        break
      case "state":
        if (formData.country && !value)
          error = "Required when country is selected"
        break
      case "city":
        if (formData.state && !value) error = "Required when state is selected"
        break
      // case "user_id":
      //   if (!patterns.user_id.test(value))
      //     error = "Invalid username (starts with letter, allows letters, spaces, &, -, 2-50 chars)";
      //   break;
      // case "password":
      //   if (value && !patterns.password.test(value))
      //     error = "Invalid password (starts with letter, allows letters, spaces, &, -, 2-50 chars)";
      //   break;
      default:
        break
    }
    return error
  }

  // Validate entire form
  const validateForm = () => {
    const errors = {}
    ;["first_name", "email"].forEach(key => {
      const error = validateField(key, formData[key])
      if (error) errors[key] = error
    })
    Object.keys(formData).forEach(key => {
      if (!["first_name", "email"].includes(key)) {
        const error = validateField(key, formData[key])
        if (error) errors[key] = error
      }
    })
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle input changes with validation
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    const error = validateField(field, value)
    setFormErrors(prev => ({ ...prev, [field]: error }))
  }

  // Handle temporary address input changes
  const handleTempAddressChange = (field, value) => {
    setTempAddressData(prev => ({ ...prev, [field]: value }))
    const error = validateField(field, value)
    setFormErrors(prev => ({ ...prev, [field]: error }))
  }

  // Transform categories to tree structure
  const transformCategoriesToTree = categories => {
    const buildTree = category => ({
      key: category.category_id,
      label: category.name,
      data: category,
      children: category.subcategories
        ? category.subcategories.map(buildTree)
        : [],
    })
    return categories.map(buildTree)
  }

  // Transform groups to tree structure
  const transformGroupsToTree = groups => {
    const buildTree = group => ({
      key: group.group_id,
      label: group.name,
      data: group,
      children: group.subgroups ? group.subgroups.map(buildTree) : [],
    })
    return groups.map(buildTree)
  }

  // Fetch categories
  const getCategories = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-categories/`)
      if (response.data && response.data.results) {
        const treeData = transformCategoriesToTree(response.data.results)
        setCategoryNodes(treeData)
      }
    } catch (error) {
      console.error("Error fetching categories:", error)
    }
  }

  // Fetch groups
  const getGroups = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-groups/`)
      if (response.data && response.data.results) {
        const groupTree = transformGroupsToTree(response.data.results)
        setGroupNodes(groupTree)
      }
    } catch (error) {
      console.error("Error fetching groups:", error)
    }
  }

  useEffect(() => {
    getCategories()
    getGroups()
  }, [])

  // Fetch dropdown options
  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-companies/`)
      const companyData = (response.data.results || []).map(item => ({
        id: item.id || item.company_id,
        name: item.name || item.company_name || item.title || "Unknown Company",
      }))
      setCompanies(companyData)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch companies"
      )
      console.error(err)
    }
  }

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`/api/v1/departments/`)
      const departmentData = (response.data.results || []).map(item => ({
        id: item.department_id,
        name: item.name || item.department_name || "Unknown Department",
      }))
      setDepartments(departmentData)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch departments"
      )
      console.error(err)
    }
  }

  const fetchHiringManagers = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-hiring-managers/`)
      const managerData = (response.data.results || []).map(item => ({
        id: item.id || item.manager_id,
        name:
          item.name || item.full_name || item.first_name || "Unknown Manager",
      }))
      setHiringManagers(managerData)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch hiring managers"
      )
      console.error(err)
    }
  }

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-countries/`)
      const countryData = (response.data.results || []).map(item => ({
        id: item.id || item.country_id,
        name: item.name || item.country_name || "Unknown Country",
      }))
      setCountries(countryData)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch countries"
      )
      console.error(err)
    }
  }

  const fetchStates = async countryId => {
    if (!countryId) return
    try {
      const response = await axios.get(
        `/api/v1/get-all-states/?country_id=${countryId}`
      )
      const stateData = (response.data.results || []).map(item => ({
        id: item.id || item.state_id,
        name: item.name || item.state_name || "Unknown State",
      }))
      setStates(stateData)
      setTempAddressData(prev => ({ ...prev, state: null, city: null }))
      setCities([])
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to fetch states"
      )
      console.error(err)
    }
  }

  const fetchCities = async stateId => {
    if (!stateId) return
    try {
      const response = await axios.get(
        `/api/v1/get-all-cities/?state_id=${stateId}`
      )
      const cityData = (response.data.results || []).map(item => ({
        id: item.id || item.city_id,
        name: item.name || item.city_name || "Unknown City",
      }))
      setCities(cityData)
      setTempAddressData(prev => ({ ...prev, city: null }))
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to fetch cities"
      )
      console.error(err)
    }
  }

  useEffect(() => {
    fetchCompanies()
    fetchDepartments()
    fetchHiringManagers()
    fetchCountries()
  }, [])

  useEffect(() => {
    if (tempAddressData.country?.id) {
      fetchStates(tempAddressData.country.id)
    }
  }, [tempAddressData.country])

  useEffect(() => {
    if (tempAddressData.state?.id) {
      fetchCities(tempAddressData.state.id)
    }
  }, [tempAddressData.state])

  useEffect(() => {
    if (selectedCompany) {
      setFormData(prev => ({ ...prev, company: { id: selectedCompany.company_id, name: selectedCompany.company_name } }))
    }
  }, [selectedCompany])

  // Handle address dialog OK button
  const handleAddressDialogOk = () => {
    const updatedAddress = [
      tempAddressData.street_address,
      tempAddressData.street_address_line_2,
      tempAddressData.city?.name,
      tempAddressData.state?.name,
      tempAddressData.country?.name,
      tempAddressData.pincode,
      tempAddressData.label?.name,
    ]
      .filter(Boolean)
      .join("\n") // Use \n for line-by-line display
    setFormData(prev => ({
      ...prev,
      street_address: tempAddressData.street_address,
      street_address_line_2: tempAddressData.street_address_line_2,
      country: tempAddressData.country,
      state: tempAddressData.state,
      city: tempAddressData.city,
      pincode: tempAddressData.pincode,
      label: tempAddressData.label,
      address: updatedAddress,
    }))
    setVisible(false)
  }

  // Handle address dialog cancel
  const handleAddressDialogCancel = () => {
    setTempAddressData({
      street_address: "",
      street_address_line_2: "",
      country: null,
      state: null,
      city: null,
      pincode: "",
      label: null,
    })
    setFormData(prev => ({
      ...prev,
      street_address: "",
      street_address_line_2: "",
      country: null,
      state: null,
      city: null,
      pincode: "",
      label: null,
      address: "",
    }))
    setVisible(false)
  }

  // Initialize tempAddressData when opening dialog
  const openAddressDialog = () => {
    setTempAddressData({
      street_address: formData.street_address,
      street_address_line_2: formData.street_address_line_2,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      pincode: formData.pincode,
      label: formData.label,
    })
    setVisible(true)
  }

  // Function to reset form fields
  const resetForm = () => {
    setFormData({
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      job_title: "",
      company: selectedCompany ? { id: selectedCompany.company_id, name: selectedCompany.company_name } : null,
      department: null,
      category: null,
      group: null,
      country: null,
      state: null,
      city: null,
      street_address: "",
      street_address_line_2: "",
      pincode: "",
      related_person: null,
      status: "Active",
      label: null,
      private: false,
      user_id: "",
      password: "",
      address: "",
    })
    setFormErrors({})
    setSelectedCategoryNode(null)
    setSelectedGroupNode(null)
    setError(null)
    setTempAddressData({
      street_address: "",
      street_address_line_2: "",
      country: null,
      state: null,
      city: null,
      pincode: "",
      label: null,
    })
  }

  const [labels, setLabels] = useState([])

  const fetchLabels = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-address-label/")
      const labelsData = response.data?.results
      setLabels(
        labelsData?.map(item => ({
          name: item?.address_label,
          code: item?.id,
        }))
      )
    } catch (error) {
      // console.error("Error fetching labels:", error)
      triggerAlert("error", "Error", "Failed to fetch address labels")
      setLabels([])
    }
  }

  useEffect(() => {
    fetchLabels()
  }, [])

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateForm()) {
      triggerAlert(
        "error",
        "Validation Error",
        "Please fix the errors in the form."
      )
      return
    }
    setLoading(true)
    setError(null)
    try {
      const payload = {
        first_name: formData.first_name,
        middle_name: formData.middle_name || "",
        last_name: formData.last_name || "",
        email: formData.email,
        phone_number: formData.phone_number || "",
        job_title: formData.job_title || "",
        company: formData.company?.id || null,
        department: formData.department?.id || null,
        category: selectedCategoryNode || null,
        group: selectedGroupNode || null,
        country: formData.country?.id || null,
        state: formData.state?.id || null,
        city: formData.city?.id || null,
        label: tempAddressData.label?.code || null,
        street_address: formData.street_address || "",
        street_address_line_2: formData.street_address_line_2 || "",
        pincode: formData.pincode || "",
        related_person: formData.related_person?.id || "",
        status: formData.status,
        password: formData.password,
        private: formData.private,
      }
        if (formData.user_id) {
    payload.user_id = formData.user_id;
  }
      

      await axios.post(`/api/v1/contacts/`, payload)
      setVisibleRightContacts(false)
      triggerAlert("success", "Success", "Contact Created Successfully!")
      resetForm()
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create contact")
      triggerAlert(
        "error",
        "Error",
        err.response?.data?.error || "Failed to create contact"
      )
      resetForm()
      setVisibleRightContacts(false)
    } finally {
      setLoading(false)
    }
  }

  const [uniqueUserId, setUniqueUserId] = useState("")
  useEffect(() => {
    const userInfo = JSON.parse(sessionStorage.getItem("user-info"))
    if (userInfo && userInfo.user_details) {
      setUniqueUserId(userInfo.user_details.unique_user_id)
    }
  }, [])

  return (
    <React.Fragment>
      <button
        type="button"
        className="btn btn-secondary import-res-btn ms-1 me-1"
        onClick={() => setVisibleRightContacts(true)}
      >
        Add Contact
      </button>

      <Sidebar
        visible={visibleRightContacts}
        position="right"
        className="sidebar"
        onHide={() => {
          setVisibleRightContacts(false)
          resetForm()
        }}
      >
        <div className="sidebar-header">
          <h3>Create a Contact</h3>
          <div className="d-flex align-items-center">
            <Tooltip
              target=".closeside"
              content="Close"
              position="bottom"
              style={{ marginBottom: "5px" }}
            />
            <Button
              icon="pi pi-times"
              className="p-button-text close-btn closeside"
              onClick={() => {
                setVisibleRightContacts(false)
                resetForm()
              }}
            />
          </div>
        </div>
        <div className="card sidebardetails">
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-danger">{error}</div>}

            <Row className="mb-3">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="firstName">
                    First Name <span className="text-danger">*</span>
                  </label>
                  <InputText
                    id="firstName"
                    placeholder="Enter First Name"
                    value={formData.first_name}
                    onChange={e =>
                      handleInputChange("first_name", e.target.value)
                    }
                    className={
                      formErrors.first_name ? "p-invalid w-full" : "w-full"
                    }
                    required
                  />
                  {formErrors.first_name && (
                    <small className="p-error">{formErrors.first_name}</small>
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="lastName">Last Name</label>
                  <InputText
                    id="lastName"
                    placeholder="Enter Last Name"
                    value={formData.last_name}
                    onChange={e =>
                      handleInputChange("last_name", e.target.value)
                    }
                    className={
                      formErrors.last_name ? "p-invalid w-full" : "w-full"
                    }
                  />
                  {formErrors.last_name && (
                    <small className="p-error">{formErrors.last_name}</small>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="middleName">Middle Name</label>
                  <InputText
                    id="middleName"
                    placeholder="Enter Middle Name (Optional)"
                    value={formData.middle_name}
                    onChange={e =>
                      handleInputChange("middle_name", e.target.value)
                    }
                    className={
                      formErrors.middle_name ? "p-invalid w-full" : "w-full"
                    }
                  />
                  {formErrors.middle_name && (
                    <small className="p-error">{formErrors.middle_name}</small>
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="email">
                    Email <span className="text-danger">*</span>
                  </label>
                  <InputText
                    id="email"
                    type="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={e => handleInputChange("email", e.target.value)}
                    className={formErrors.email ? "p-invalid w-full" : "w-full"}
                    required
                  />
                  {formErrors.email && (
                    <small className="p-error">{formErrors.email}</small>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <InputText
                    id="phoneNumber"
                    placeholder="Enter Phone Number"
                    value={formData.phone_number}
                    onChange={e =>
                      handleInputChange("phone_number", e.target.value)
                    }
                    className={
                      formErrors.phone_number ? "p-invalid w-full" : "w-full"
                    }
                  />
                  {formErrors.phone_number && (
                    <small className="p-error">{formErrors.phone_number}</small>
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="jobTitle">Designation</label>
                  <InputText
                    id="jobTitle"
                    placeholder="Enter Job Title"
                    value={formData.job_title}
                    onChange={e =>
                      handleInputChange("job_title", e.target.value)
                    }
                    className={
                      formErrors.job_title ? "p-invalid w-full" : "w-full"
                    }
                  />
                  {formErrors.job_title && (
                    <small className="p-error">{formErrors.job_title}</small>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="company">Company</label>
                  <InputText
                    id="company"
                    value={formData.company?.name || ''}
                    readOnly
                    className="w-full"
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="department">Department</label>
                  <Dropdown
                    id="department"
                    value={formData.department}
                    onChange={e => handleInputChange("department", e.value)}
                    options={departments}
                    optionLabel="name"
                    optionValue={option => option}
                    placeholder="Select Department"
                    filter
                    className={
                      formErrors.department
                        ? "p-invalid w-full bgclr"
                        : "w-full bgclr"
                    }
                    disabled={!departments.length}
                  />
                  {formErrors.department && (
                    <small className="p-error">{formErrors.department}</small>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="company">Categories</label>
                  <TreeSelect
                    value={selectedCategoryNode}
                    options={categoryNodes}
                    onChange={e => setSelectedCategoryNode(e.value)}
                    className="w-full"
                    placeholder="Select Category"
                    filter
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="company">Group</label>
                  <TreeSelect
                    value={selectedGroupNode}
                    options={groupNodes}
                    onChange={e => setSelectedGroupNode(e.value)}
                    className="w-full"
                    placeholder="Select Group"
                    filter
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={12}>
                <div
                  className="p-field companie-add"
                  style={{ position: "relative" }}
                >
                  <label htmlFor="address">Address</label>
                  <InputTextarea
                    id="address"
                    value={formData.address}
                    readOnly
                    style={{ paddingRight: "2rem" }}
                    rows={3}
                    cols={30}
                    className={
                      formErrors.address ? "p-invalid w-full" : "w-full"
                    }
                  />
                  <i
                    className="pi pi-pencil"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "70%",
                      transform: "translateY(-50%)",
                      color: "#6c757d",
                      cursor: "pointer",
                    }}
                    onClick={openAddressDialog}
                  />
                  <Dialog
                    header="Edit Address"
                    className="address-popup"
                    visible={visible}
                    onHide={handleAddressDialogCancel}
                    style={{ width: "30vw" }}
                    breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                    footer={
                      <div>
                        <Button
                          label="OK"
                          icon="pi pi-check"
                          onClick={handleAddressDialogOk}
                          className="p-button-primary"
                        />
                      </div>
                    }
                  >
                    <div className="card sidebardetails">
                      <form>
                        <Row className="mb-3">
                          <Col lg={6}>
                            <div className="p-field">
                              <label htmlFor="street_address">Street 1</label>
                              <InputText
                                id="street_address"
                                value={tempAddressData.street_address}
                                onChange={e =>
                                  handleTempAddressChange(
                                    "street_address",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Street 1"
                                className={
                                  formErrors.street_address
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                              />
                              {formErrors.street_address && (
                                <small className="p-error">
                                  {formErrors.street_address}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="p-field">
                              <label htmlFor="street_address_line_2">
                                Street 2
                              </label>
                              <InputText
                                id="street_address_line_2"
                                value={tempAddressData.street_address_line_2}
                                onChange={e =>
                                  handleTempAddressChange(
                                    "street_address_line_2",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Street 2 (Optional)"
                                className={
                                  formErrors.street_address_line_2
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                              />
                              {formErrors.street_address_line_2 && (
                                <small className="p-error">
                                  {formErrors.street_address_line_2}
                                </small>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col lg={6}>
                            <div className="p-field">
                              <label htmlFor="country">Country</label>
                              <Dropdown
                                id="country"
                                value={tempAddressData.country}
                                onChange={e =>
                                  handleTempAddressChange("country", e.value)
                                }
                                options={countries}
                                optionLabel="name"
                                optionValue={option => option}
                                filter
                                filterPlaceholder="Search Country"
                                className={
                                  formErrors.country
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                                placeholder="Select Country"
                                disabled={!countries.length}
                              />
                              {formErrors.country && (
                                <small className="p-error">
                                  {formErrors.country}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="p-field">
                              <label htmlFor="state">State</label>
                              <Dropdown
                                id="state"
                                value={tempAddressData.state}
                                onChange={e =>
                                  handleTempAddressChange("state", e.value)
                                }
                                options={states}
                                optionLabel="name"
                                optionValue={option => option}
                                filter
                                filterPlaceholder="Search State"
                                className={
                                  formErrors.state
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                                placeholder="Select State"
                                disabled={
                                  !tempAddressData.country || !states.length
                                }
                              />
                              {formErrors.state && (
                                <small className="p-error">
                                  {formErrors.state}
                                </small>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col lg={6}>
                            <div className="p-field">
                              <label htmlFor="city">City</label>
                              <Dropdown
                                id="city"
                                value={tempAddressData.city}
                                onChange={e =>
                                  handleTempAddressChange("city", e.value)
                                }
                                options={cities}
                                optionLabel="name"
                                optionValue={option => option}
                                filter
                                filterPlaceholder="Search City"
                                className={
                                  formErrors.city
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                                placeholder="Select City"
                                disabled={
                                  !tempAddressData.state || !cities.length
                                }
                              />
                              {formErrors.city && (
                                <small className="p-error">
                                  {formErrors.city}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="p-field">
                              <label htmlFor="pincode">Postal Code</label>
                              <InputText
                                id="pincode"
                                value={tempAddressData.pincode}
                                onChange={e =>
                                  handleTempAddressChange(
                                    "pincode",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Postal Code"
                                className={
                                  formErrors.pincode
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                              />
                              {formErrors.pincode && (
                                <small className="p-error">
                                  {formErrors.pincode}
                                </small>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col lg={12}>
                            <div className="pist-field">
                              <label htmlFor="label">Label</label>
                              <Dropdown
                                id="label"
                                value={tempAddressData.label}
                                onChange={e =>
                                  handleTempAddressChange("label", e.value)
                                }
                                options={labels}
                                optionLabel="name"
                                optionValue={option => option}
                                placeholder="Select Label"
                                className={
                                  formErrors.label
                                    ? "p-invalid w-full activejobdrop"
                                    : "w-full activejobdrop"
                                }
                              />
                              {formErrors.label && (
                                <small className="p-error">
                                  {formErrors.label}
                                </small>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </form>
                    </div>
                  </Dialog>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="username">Username</label>
                  <InputText
                    id="username"
                    placeholder="Enter username"
                    value={formData.user_id}
                    onChange={e => handleInputChange("user_id", e.target.value)}
                    // className={formErrors.user_id ? "p-invalid w-full" : "w-full"}
                    // required
                  />
                  {/* {formErrors.user_id && <small className="p-error">{formErrors.user_id}</small>} */}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="password">Password</label>
                  <InputText
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={e =>
                      handleInputChange("password", e.target.value)
                    }
                    // className={formErrors.password ? "p-invalid w-full" : "w-full"}
                  />
                  {/* {formErrors.password && <small className="p-error">{formErrors.password}</small>} */}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="p-field">
                  <label htmlFor="related_person">Reporting Person</label>
                  <Dropdown
                    id="related_person"
                    value={formData.related_person}
                    onChange={e => handleInputChange("related_person", e.value)}
                    options={hiringManagers}
                    optionLabel="name"
                    optionValue={option => option}
                    placeholder="Select Reporting Person"
                    className={
                      formErrors.related_person
                        ? "p-invalid w-full bgclr"
                        : "w-full bgclr"
                    }
                    disabled={!hiringManagers.length}
                  />
                  {formErrors.related_person && (
                    <small className="p-error">
                      {formErrors.related_person}
                    </small>
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field pt-4">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.private}
                      onChange={e =>
                        handleInputChange("private", e.target.checked)
                      }
                      className="me-2"
                    />
                    Private
                  </label>
                </div>
              </Col>

              <Col lg={6}>
                <div className="p-field mt-3">
                  <label htmlFor="jobType">UserIDs</label>
                  <InputText
                    value={uniqueUserId}
                    readOnly
                    placeholder="Select a User ID"
                    className="w-full activejobdrop"
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <Button
                  type="submit"
                  color="primary"
                  className="btn btn-primary waves-effect waves-light me-2 sidebarbtn float-end"
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create"}
                </Button>
              </Col>
            </Row>
          </form>
        </div>
      </Sidebar>
    </React.Fragment>
  )
}

export default AddContact
